import "./styles.scss";
import { useEffect, useState, useCallback, useRef, useContext } from "react";
import BarMenu from "../BarMenu";
import UploadFile from "./UploadFile";
import api from "../../utils/api";
import { Context } from "../../context/Contex";

export default function LoadData() {
    const { refreshUnreadCount } = useContext(Context);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [progress, setProgress] = useState({
        current: 0,
        total: 0,
        percentage: 0,
        itemsProcessed: 0,
        totalItems: 0
    });
    const [resetInfo, setResetInfo] = useState(null);
    const [resetOption, setResetOption] = useState(true);
    const [secondaryCount, setSecondaryCount] = useState(0);
    const [removedAwbs, setRemovedAwbs] = useState([]);

    const resetOptionRef = useRef(resetOption);
    const serviceTypeFiltersRef = useRef(['CARD']);

    useEffect(() => {
        resetOptionRef.current = resetOption;
    }, [resetOption]);

    useEffect(() => {
        api.get('/config/service-type-filters')
            .then(res => {
                if (res.data?.success && res.data.data?.length > 0) {
                    serviceTypeFiltersRef.current = res.data.data.map(f => f.value.toUpperCase().trim());
                }
            })
            .catch(() => {
                // mantém o padrão ['CARD'] já definido no ref
            });
    }, []);

    // =====================================================================
    // 🔥 FUNÇÃO PARA REMOVER DUPLICATAS POR AWB (MANTÉM O MAIS RECENTE)
    // =====================================================================
    const formatDateRaw = (raw) => {
        if (!raw || raw === 'sem data') return 'sem data';
        const s = String(raw).trim();

        // Serial date do Excel (ex: 46128)
        if (!isNaN(s) && s.length <= 5) {
            const date = new Date(Date.UTC(1899, 11, 30) + parseFloat(s) * 86400000);
            if (!isNaN(date.getTime())) {
                const d = String(date.getUTCDate()).padStart(2, '0');
                const m = String(date.getUTCMonth() + 1).padStart(2, '0');
                const y = date.getUTCFullYear();
                return `${d}/${m}/${y}`;
            }
        }

        // Já está em DD/MM/AAAA — retorna direto
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

        // Formato ISO ou qualquer coisa que o JS consiga parsear (YYYY-MM-DD, etc.)
        const parsed = new Date(s);
        if (!isNaN(parsed.getTime())) {
            const d = String(parsed.getUTCDate()).padStart(2, '0');
            const m = String(parsed.getUTCMonth() + 1).padStart(2, '0');
            const y = parsed.getUTCFullYear();
            return `${d}/${m}/${y}`;
        }

        return s;
    };

    const deduplicateByAWB = (data) => {
        if (!Array.isArray(data) || data.length === 0) return {
            dedupData: data,
            duplicatesRemoved: 0,
            removedItems: []
        };

        const awbMap = new Map();
        let duplicatesCount = 0;
        const removedItems = [];

        data.forEach(item => {
            // Procurar a coluna AWB (pode ter variações de nome)
            const awb = (
                item["AWB"] ||
                item["Awb"] ||
                item["awb"] ||
                item["AWB "] ||
                ""
            ).toString().trim();

            // Se não tem AWB, mantém o registro com chave única
            if (!awb) {
                awbMap.set(`no-awb-${Math.random()}`, item);
                return;
            }

            // Procurar a data de criação (pode ter variações)
            const dataCriacaoRaw = (
                item["Data de criação"] ||   // ← adiciona essa primeiro
                item["DATA CRIAÇÃO"] ||
                item["DATA CRIACAO"] ||
                item["Data Criação"] ||
                item["Data de Criação"] ||
                item["DATA_CRIACAO"] ||
                ""
            ).toString().trim();

            // Converter data para timestamp para comparação
            let timestamp = 0;
            if (dataCriacaoRaw) {
                // Se for número (serial date do Excel)
                if (!isNaN(dataCriacaoRaw) && dataCriacaoRaw.length < 10) {
                    timestamp = parseFloat(dataCriacaoRaw);
                } else {
                    // Tentar parsear como data normal
                    const parsed = new Date(dataCriacaoRaw);
                    timestamp = isNaN(parsed.getTime()) ? 0 : parsed.getTime();
                }
            }

            const getAssunto = (item) => (
                item["ASSUNTO"] || item["Assunto"] || item["assunto"] || awb
            ).toString().trim();

            // Se já existe essa AWB
            if (awbMap.has(awb)) {
                duplicatesCount++;
                const existing = awbMap.get(awb);
                const existingDateRaw = (
                    existing["Data de criação"] ||
                    existing["DATA CRIAÇÃO"] ||
                    existing["DATA CRIACAO"] ||
                    existing["Data Criação"] ||
                    existing["Data de Criação"] ||
                    existing["DATA_CRIACAO"] ||
                    ""
                ).toString().trim();

                let existingTimestamp = 0;
                if (existingDateRaw) {
                    if (!isNaN(existingDateRaw) && existingDateRaw.length < 10) {
                        existingTimestamp = parseFloat(existingDateRaw);
                    } else {
                        const parsed = new Date(existingDateRaw);
                        existingTimestamp = isNaN(parsed.getTime()) ? 0 : parsed.getTime();
                    }
                }

                // Mantém o mais recente
                if (timestamp > existingTimestamp) {
                    removedItems.push({
                        awb: awb,
                        motivo: `Duplicada`,
                        ficou: `${getAssunto(item)} | ${formatDateRaw(dataCriacaoRaw) || 'sem data'}`,
                        ignorada: `${getAssunto(existing)} | ${formatDateRaw(existingDateRaw) || 'sem data'}`
                    });
                    awbMap.set(awb, item);
                } else {
                    removedItems.push({
                        awb: awb,
                        motivo: `Duplicada`,
                        ficou: `${getAssunto(existing)} | ${formatDateRaw(existingDateRaw) || 'sem data'}`,
                        ignorada: `${getAssunto(item)} | ${formatDateRaw(dataCriacaoRaw) || 'sem data'}`
                    });
                }
            } else {
                // Primeira vez que vemos essa AWB
                awbMap.set(awb, item);
            }
        });

        return {
            dedupData: Array.from(awbMap.values()),
            duplicatesRemoved: duplicatesCount,
            removedItems
        };
    };

    const checkedDataByServiceType = (arr) => {
        if (!Array.isArray(arr)) {
            return {
                filteredData: [],
                duplicatesRemoved: 0,
                removedItems: []
            };
        }

        // Mantém todos os dados originais
        const filtered = [...arr];

        // Remove duplicatas baseado no AWB
        const {
            dedupData,
            duplicatesRemoved,
            removedItems: dupRemovedItems
        } = deduplicateByAWB(filtered);

        // Adiciona informação da origem da remoção
        const dedupWithSource = dupRemovedItems.map(item => ({
            ...item,
            source: 'duplicata'
        }));

        // Retorno final
        return {
            filteredData: dedupData,
            duplicatesRemoved,
            removedItems: dedupWithSource
        };
    };


    // =====================================================================
    // 📤 ENVIAR DADOS PRIMÁRIOS (POWER BI)
    // =====================================================================
    const sendPrimaryData = useCallback(async (rawData) => {
        if (!rawData || rawData.length === 0) return;

        const currentResetOption = resetOptionRef.current;

        setLoading(true);

        setRemovedAwbs([]);
        const modeText = currentResetOption ? 'substituindo dados' : 'mesclando dados';
        setMessage({ type: 'info', text: `📤 Processando arquivo principal (${modeText})...` });

        try {
            const { filteredData: processedData, duplicatesRemoved, removedItems } = checkedDataByServiceType(rawData);

            if (processedData.length === 0) {
                const allowedList = serviceTypeFiltersRef.current.join(', ');
                setMessage({
                    type: 'error',
                    text: `❌ Nenhuma linha passou pelos filtros de tipo de serviço. Tipos aceitos: ${allowedList}. Verifique se a coluna "Tipo Serviço" do arquivo contém esses valores.`
                });
                if (removedItems.length > 0) setRemovedAwbs(removedItems);
                setLoading(false);
                return;
            }

            const CHUNK_SIZE = 100;
            const chunks = [];
            for (let i = 0; i < processedData.length; i += CHUNK_SIZE) {
                chunks.push(processedData.slice(i, i + CHUNK_SIZE));
            }

            let totalInserted = 0;
            let totalSkipped = 0;
            let totalInvalid = 0;
            let totalUpdated = 0;
            let allBackendRejected = [];

            for (let i = 0; i < chunks.length; i++) {
                const percentage = Math.round(((i + 1) / chunks.length) * 100);
                setProgress({
                    current: i + 1,
                    total: chunks.length,
                    percentage,
                    itemsProcessed: totalInserted,
                    totalItems: processedData.length
                });

                setMessage({ type: 'info', text: `📤 Enviando lote ${i + 1} de ${chunks.length}...` });

                const response = await api.post('/conjunto-itens/load-data', {
                    data: chunks[i],
                    isFirstChunk: i === 0,
                    isLastChunk: i === chunks.length - 1,
                    shouldReset: currentResetOption
                });

                if (response.data.success) {
                    totalInserted += response.data.insertedCount || 0;
                    totalSkipped += response.data.skippedCount || 0;
                    totalInvalid += response.data.invalidCount || 0;
                    totalUpdated += response.data.updatedCount || 0;

                    if (response.data.rejectedItems?.length) {
                        allBackendRejected = allBackendRejected.concat(
                            response.data.rejectedItems.map(r => ({
                                awb: r.awb,
                                motivo: r.reason,
                                source: r.source || 'servidor'
                            }))
                        );
                    }

                    if (i === 0 && currentResetOption) {
                        setResetInfo({
                            deletedConjuntoCount: response.data.deletedConjuntoCount || 0,
                            deletedItensCount: response.data.deletedItensCount || 0,
                            deletedStatsCount: response.data.deletedStatsCount || 0
                        });
                    }
                }

                await new Promise(resolve => setTimeout(resolve, 50));
            }

            const allRemoved = [...removedItems, ...allBackendRejected];
            if (allRemoved.length > 0) setRemovedAwbs(allRemoved);

            const parts = [];
            if (currentResetOption) parts.push(`${totalInserted} registros inseridos (reset)`);
            else parts.push(`${totalInserted} novos inseridos`);
            if (totalUpdated > 0) parts.push(`${totalUpdated} atualizados`);
            if (duplicatesRemoved > 0) parts.push(`${duplicatesRemoved} duplicatas removidas`);
            if (totalSkipped > 0) parts.push(`${totalSkipped} já existiam`);
            if (totalInvalid > 0) parts.push(`${totalInvalid} rejeitados pelo servidor`);

            setMessage({ type: 'success', text: `✅ Upload principal concluído — ${parts.join(' | ')}` });
            refreshUnreadCount();

        } catch (error) {
            console.error('Erro ao enviar dados primários:', error);
            const errorMsg = error.response?.data?.message || error.message;
            setMessage({ type: 'error', text: `❌ Erro ao enviar: ${errorMsg}` });
        } finally {
            setLoading(false);
            setTimeout(() => {
                setProgress({ current: 0, total: 0, percentage: 0, itemsProcessed: 0, totalItems: 0 });

            }, 5000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshUnreadCount]);

    // =====================================================================
    // 📎 ENVIAR DADOS SECUNDÁRIOS (ICS)
    // =====================================================================
    const sendSecondaryData = useCallback(async (rawData) => {
        if (!rawData || rawData.length === 0) return;

        const currentNumber = secondaryCount + 1;
        setSecondaryCount(currentNumber);

        setLoading(true);

        setRemovedAwbs([]);
        setMessage({ type: 'info', text: `📎 Processando arquivo secundário ${currentNumber}...` });

        try {
            const { filteredData: processedData, duplicatesRemoved, removedItems } = checkedDataByServiceType(rawData);

            if (processedData.length === 0) {
                const allowedList = serviceTypeFiltersRef.current.join(', ');
                setMessage({
                    type: 'error',
                    text: `❌ Nenhuma linha passou pelos filtros de tipo de serviço. Tipos aceitos: ${allowedList}. Verifique se a coluna "Tipo Serviço" do arquivo contém esses valores.`
                });
                if (removedItems.length > 0) setRemovedAwbs(removedItems);
                setLoading(false);
                return;
            }

            const CHUNK_SIZE = 100;
            const chunks = [];
            for (let i = 0; i < processedData.length; i += CHUNK_SIZE) {
                chunks.push(processedData.slice(i, i + CHUNK_SIZE));
            }

            let totalInserted = 0;
            let totalUpdated = 0;  // ← novo
            let totalInvalid = 0;
            let allBackendRejected = [];

            for (let i = 0; i < chunks.length; i++) {
                const percentage = Math.round(((i + 1) / chunks.length) * 100);
                setProgress({
                    current: i + 1,
                    total: chunks.length,
                    percentage,
                    itemsProcessed: totalInserted,
                    totalItems: processedData.length
                });

                setMessage({ type: 'info', text: `📎 Secundário ${currentNumber}: Enviando lote ${i + 1} de ${chunks.length}...` });

                const response = await api.post('/conjunto-itens/add-secondary', {
                    data: chunks[i],
                    isFirstChunk: i === 0,
                    isLastChunk: i === chunks.length - 1
                });

                if (response.data.success) {
                    totalInserted += response.data.insertedCount || 0;
                    totalUpdated += response.data.updatedCount || 0;  // ← novo
                    totalInvalid += response.data.invalidCount || 0;

                    if (response.data.rejectedItems?.length) {
                        allBackendRejected = allBackendRejected.concat(
                            response.data.rejectedItems.map(r => ({
                                awb: r.awb,
                                motivo: r.reason,
                                source: r.source || 'servidor'
                            }))
                        );
                    }

                    // ← novo: adiciona atualizados ao relatório
                    if (response.data.updatedAwbs?.length) {
                        allBackendRejected = allBackendRejected.concat(
                            response.data.updatedAwbs.map(r => ({
                                awb: r.awb,
                                motivo: r.motivo || 'Registro atualizado com dados do ICS',
                                source: 'atualizado'
                            }))
                        );
                    }
                }

                await new Promise(resolve => setTimeout(resolve, 50));
            }

            const allRemoved = [...removedItems, ...allBackendRejected];
            if (allRemoved.length > 0) setRemovedAwbs(allRemoved);

            const parts = [`${totalInserted} novos inseridos`];
            if (totalUpdated > 0) parts.push(`${totalUpdated} atualizados`);  // ← novo
            if (duplicatesRemoved > 0) parts.push(`${duplicatesRemoved} duplicatas removidas`);
            if (totalInvalid > 0) parts.push(`${totalInvalid} rejeitados pelo servidor`);

            setMessage({ type: 'success', text: `✅ Secundário ${currentNumber} concluído — ${parts.join(' | ')}` });
            refreshUnreadCount();

        } catch (error) {
            console.error('Erro ao enviar dados secundários:', error);
            const errorMsg = error.response?.data?.message || error.message;
            setMessage({ type: 'error', text: `❌ Erro ao enviar: ${errorMsg}` });
        } finally {
            setLoading(false);
            setTimeout(() => {
                setProgress({ current: 0, total: 0, percentage: 0, itemsProcessed: 0, totalItems: 0 });
            }, 5000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondaryCount, refreshUnreadCount]);


    return (
        <div id="LoadData">
            <BarMenu />
            <section>
                <h2>Carregar Dados de Conjunto de Itens</h2>

                <div className="reset-option">
                    <label>
                        <input
                            type="checkbox"
                            checked={resetOption}
                            onChange={() => setResetOption(!resetOption)}
                            disabled={loading}
                        />
                        <span>Apagar todos os dados antes de carregar (ConjuntoItens, Itens, Estatísticas)</span>
                    </label>
                </div>

                <div className={`upload-section ${loading ? 'uploading' : ''}`}>
                    <div className={`upload-primary ${loading ? 'disabled' : ''}`}>
                        <h3>📤 Upload Principal ( Power BI ) </h3>
                        <UploadFile
                            setProcessedData={sendPrimaryData}
                            headerRow={3}
                            fileType="xlsx"
                            disabled={loading}
                            dynamicHeaderRow={true}
                        />
                        {loading && <div className="upload-overlay">🔒 Aguarde o envio atual...</div>}
                    </div>

                    <div className={`upload-secondary ${loading ? 'disabled' : ''}`}>
                        <h3>📎 Upload Secundário ( ICS )</h3>
                        <p className="upload-hint">
                            {secondaryCount === 0
                                ? "Envie os arquivos CSV um por vez"
                                : `${secondaryCount} arquivo(s) já enviado(s) - pode enviar mais`
                            }
                        </p>
                        <UploadFile
                            setProcessedData={sendSecondaryData}
                            headerRow={1}
                            fileType="csv"
                            disabled={loading}
                        />
                        {loading && <div className="upload-overlay">🔒 Aguarde o envio atual...</div>}
                    </div>
                </div>

                {message.text && (
                    <div className={`feedback-message ${message.type}`}>
                        <span>{message.text}</span>
                    </div>
                )}

                {loading && progress.total > 0 && (
                    <div className="progress-container">
                        <div className="progress-header">
                            <div className="progress-title">⏳ Enviando dados...</div>
                            <div className="progress-percentage">{progress.percentage}%</div>
                        </div>
                        <div className="progress-bar-wrapper">
                            <div className="progress-bar-fill" style={{ width: `${progress.percentage}%` }}>
                                {progress.percentage > 10 && `${progress.percentage}%`}
                            </div>
                        </div>
                        <div className="progress-details">
                            <div className="progress-detail-item"><strong>Lotes:</strong> {progress.current} / {progress.total}</div>
                            <div className="progress-detail-item"><strong>Registros:</strong> {progress.itemsProcessed} / {progress.totalItems}</div>
                        </div>
                        <div className="progress-warning">⚠️ <strong>Não feche esta página</strong> até concluir.</div>
                    </div>
                )}

                {/* RELATÓRIO DE AWBs REMOVIDAS/IGNORADAS */}
                {removedAwbs.length > 0 && (() => {
                    const bySource = {
                        tipo_servico: removedAwbs.filter(i => i.source === 'tipo_servico'),
                        duplicata: removedAwbs.filter(i => i.source === 'duplicata'),
                        atualizado: removedAwbs.filter(i => i.source === 'atualizado'),  // ← novo
                        servidor: removedAwbs.filter(i => !['tipo_servico', 'duplicata', 'atualizado'].includes(i.source)),
                    };
                    const categories = [
                        { key: 'tipo_servico', label: 'Tipo de Serviço', color: '#d97706', bg: '#fff7ed', border: '#fed7aa', icon: '⛔' },
                        { key: 'duplicata', label: 'Duplicatas', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: '🔁' },
                        { key: 'atualizado', label: 'Atualizados (ICS)', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: '🔄' },  // ← novo
                        { key: 'servidor', label: 'Rejeitado pelo Servidor', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: '❌' },
                    ].filter(c => bySource[c.key].length > 0);

                    return (
                        <div className="removed-awbs-section">
                            <div className="removed-awbs-header">
                                <div className="removed-awbs-title">
                                    <span className="removed-awbs-icon">📋</span>
                                    <span>Relatório de Linhas Ignoradas</span>
                                    <span className="removed-awbs-total-badge">{removedAwbs.length}</span>
                                </div>
                                <button
                                    className="removed-awbs-close"
                                    onClick={() => setRemovedAwbs([])}
                                    title="Fechar relatório"
                                >✕</button>
                            </div>

                            <div className="removed-summary">
                                {categories.map(cat => (
                                    <div key={cat.key} className="summary-badge" style={{ background: cat.bg, border: `1px solid ${cat.border}`, color: cat.color }}>
                                        <span className="badge-icon">{cat.icon}</span>
                                        <span className="badge-count">{bySource[cat.key].length}</span>
                                        <span className="badge-label">{cat.label}</span>
                                    </div>
                                ))}
                            </div>

                            {categories.map(cat => (
                                <details key={cat.key} className="removed-category" open={bySource[cat.key].length <= 20}>
                                    <summary className="category-summary" style={{ borderLeft: `4px solid ${cat.color}` }}>
                                        <span className="category-label">{cat.icon} {cat.label}</span>
                                        <span className="category-count" style={{ background: cat.bg, color: cat.color, border: `1px solid ${cat.border}` }}>
                                            {bySource[cat.key].length} linha{bySource[cat.key].length !== 1 ? 's' : ''}
                                        </span>
                                    </summary>
                                    <div className="table-wrapper">
                                        <table className="removed-awbs-table">
                                            <thead style={{ background: cat.color }}>
                                                <tr>
                                                    <th>#</th>
                                                    <th>AWB</th>
                                                    <th>MOTIVO</th>
                                                    {bySource[cat.key].some(i => i.ficou) && <th>FICOU</th>}
                                                    {bySource[cat.key].some(i => i.ignorada) && <th>IGNORADA</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bySource[cat.key].map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td className="awb-cell">{item.awb}</td>
                                                        <td className="motivo-cell">{item.motivo}</td>
                                                        {bySource[cat.key].some(i => i.ficou) && (
                                                            <td className="motivo-cell">{item.ficou || '-'}</td>
                                                        )}
                                                        {bySource[cat.key].some(i => i.ignorada) && (
                                                            <td className="motivo-cell">{item.ignorada || '-'}</td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </details>
                            ))}
                        </div>
                    );
                })()}

                <div className="info-card">
                    <h4><span>ℹ️</span> Informações Importantes</h4>
                    <ul>
                        <li><strong>Upload Principal (XLSX):</strong> dados vindo do Power BI</li>
                        <li><strong>Upload Secundário (CSV):</strong> dados vindo do painel ticket <strong>ICS</strong> (envie quantos arquivos precisar)</li>
                        <li>Cada arquivo é enviado <strong>automaticamente</strong> após o upload</li>
                        <li><strong>Com checkbox marcado:</strong> Apaga tudo e insere do zero</li>
                        <li><strong>Com checkbox desmarcado:</strong> Apenas adiciona novos (ignora existentes)</li>
                        <li>Lotes de <strong>100 registros</strong></li>
                        <li>Linhas sem campo <strong>"Assunto"</strong> são automaticamente ignoradas</li>
                        <li>
                            Apenas tipos de serviço permitidos são processados:{' '}
                            {serviceTypeFiltersRef.current.map((t, i) => (
                                <strong key={i}>{i > 0 ? ', ' : ''}{t}</strong>
                            ))}
                            {' '}— configure em <strong>Config &gt; Tipos de Serviço</strong>
                        </li>
                        <li>AWBs duplicadas são removidas automaticamente (mantido o registro com <strong>DATA CRIAÇÃO</strong> mais recente)</li>
                    </ul>
                </div>
            </section>
        </div>
    );
}